import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { Box, CircularProgress, Typography } from '@mui/material';
import { isUserInWorkspace } from '@/lib/auth/domain-validation';

/**
 * 認証コールバックページ
 * OIDC認証後のリダイレクトを処理し、ワークスペース認証チェックを実行
 *
 * react-oidc-context v3.xでは、認証コールバックはAuthProviderが自動的に処理するため、
 * このページでは認証状態の変化を監視してリダイレクトを行う
 */
export function CallbackPage() {
  const auth = useOidcAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // 二重処理を防止
    if (hasProcessed.current) {
      return;
    }

    // ローディング中は待機
    if (auth.isLoading) {
      return;
    }

    // エラーがある場合
    if (auth.error) {
      hasProcessed.current = true;
      console.error('認証エラー:', auth.error);
      setError(auth.error.message);
      navigate('/auth/error?error=Signin', { replace: true });
      return;
    }

    // 認証成功後の処理
    if (auth.isAuthenticated && auth.user?.profile) {
      hasProcessed.current = true;

      const userProfile = {
        email: auth.user.profile.email || auth.user.profile.preferred_username || null,
        name: auth.user.profile.name || auth.user.profile.preferred_username || null,
      };

      // ワークスペース認証チェック
      const hasWorkspaceAccess = isUserInWorkspace(userProfile);

      if (!hasWorkspaceAccess) {
        console.warn('❌ ワークスペースアクセス権限がありません:', userProfile.email);
        // ユーザーを削除してエラーページへリダイレクト
        auth.removeUser().then(() => {
          navigate('/auth/error?error=AccessDenied', { replace: true });
        });
        return;
      }

      // 認証成功: 保存されたURLまたはホームページへリダイレクト
      const returnUrl = sessionStorage.getItem('auth_return_url');
      sessionStorage.removeItem('auth_return_url');
      console.log('✅ 認証成功:', userProfile.email);
      navigate(returnUrl || '/', { replace: true });
      return;
    }

    // 未認証の場合（コールバック処理が完了していない可能性）
    if (!auth.isAuthenticated && !auth.isLoading) {
      // 少し待ってから再チェック（認証処理の完了を待つ）
      const timeout = setTimeout(() => {
        if (!auth.isAuthenticated && !hasProcessed.current) {
          hasProcessed.current = true;
          console.warn('認証が完了しませんでした');
          navigate('/auth/signin', { replace: true });
        }
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.error, auth.user, navigate]);

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      gap={2}
    >
      <CircularProgress />
      <Typography>認証を処理中...</Typography>
    </Box>
  );
}
